import { Fragment, useEffect, useState, useCallback } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function ImageSelectMenu({
  label,
  options,
  value, // This now comes from Controller
  onChange, // Directly use the onChange from Controller
  error,
}) {
  // Find the option that matches the value
  const selected = options.find((option) => option.id === value) || {
    id: null,
    logo_url: null,
    customer_name: "Please select",
  };

  return (
    <Listbox
      value={selected}
      onChange={(selectedOption) => onChange(selectedOption.id)}
    >
      {({ open }) => (
        <>
          <div>
            <Listbox.Label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-300">
              {label}
            </Listbox.Label>
            <div className="relative mt-2">
              <Listbox.Button className="relative w-full cursor-default rounded-md bg-gray-50 dark:bg-gray-800 py-2.5 pl-3 pr-10 text-left text-gray-600 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.06] focus:outline-none transition-colors duration-150 text-sm leading-none font-normal">
                <span className="flex items-center">
                  {selected.logo_url ? (
                    <img
                      src={selected.logo_url}
                      alt=""
                      className="h-5 w-5 flex-shrink-0 rounded-full"
                    />
                  ) : (
                    <div className="h-5 w-5 flex-shrink-0 rounded-full bg-gray-100 border border-gray-200"></div>
                  )}
                  <span className="ml-3 block truncate">
                    {selected.customer_name}
                  </span>
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
                  <ChevronUpDownIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </span>
              </Listbox.Button>

              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-50 mt-1 py-4 max-h-56 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 text-base shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 focus:outline-none sm:text-sm">
                  {options.map((option) => (
                    <Listbox.Option
                      key={option.id}
                      className={({ active }) =>
                        classNames(
                          active ? "bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-200" : "text-gray-900 dark:text-gray-200",
                          "relative cursor-default select-none py-2 pl-3 pr-9"
                        )
                      }
                      value={option}
                    >
                      {({ selected, active }) => (
                        <>
                          <div className="flex items-center">
                            {option.logo_url ? (
                              <img
                                src={option.logo_url}
                                alt=""
                                className="h-5 w-5 flex-shrink-0 rounded-full"
                              />
                            ) : (
                              <div className="h-5 w-5 flex-shrink-0 rounded-full bg-gray-100 border border-gray-200"></div>
                            )}
                            <span
                              className={classNames(
                                selected ? "font-semibold" : "font-normal",
                                "ml-3 block truncate"
                              )}
                            >
                              {option.customer_name}
                            </span>
                          </div>

                          {selected ? (
                            <span
                              className={classNames(
                                active ? "text-gray-400 dark:text-gray-500" : "text-gray-600 dark:text-gray-300",
                                "absolute inset-y-0 right-0 flex items-center pr-4"
                              )}
                            >
                              <CheckIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
            {error && <p className="h-2 pt-1 text-sm">{error.message}</p>}
          </div>
        </>
      )}
    </Listbox>
  );
}

export { ImageSelectMenu };
